import React, { useState, useRef } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

function Social({ website, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState((website && (website.socials?.items || [])) || []);
  const [dragIndex, setDragIndex] = useState(-1);
  const [overIndex, setOverIndex] = useState(-1);
  const itemRefs = useRef([]);

  const socialOptions = [
    'Facebook', 'Twitter', 'LinkedIn', 'GitHub', 'Instagram', 'YouTube', 'Tumblr', 'Pinterest', 'Reddit', 'Telegram', 'Email', 'Phone'
  ];

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const addItem = () => {
    setItems(prev => [...prev, { text: '', url: '', social: '' }]);
  };

  const removeItem = (idx) => {
    setItems(prev => prev.filter((_, i) => i !== idx));
  };

  const updateField = (idx, field, value) => {
    setItems(prev => prev.map((it, i) => i === idx ? { ...it, [field]: value } : it));
  };

  const moveItem = (from, to) => {
    setItems(prev => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (idx, e) => {
    setDragIndex(idx);
    setOverIndex(idx);
    try { e.dataTransfer.setData('text/plain', String(idx)); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = itemRefs.current[idx];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (idx) => {
    if (idx !== overIndex) setOverIndex(idx);
  };

  const onDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDrop = (idx, e) => {
    e.preventDefault();
    const from = dragIndex;
    const to = idx;
    if (from !== -1 && to !== -1 && from !== to) moveItem(from, to);
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const onDragEnd = () => {
    setDragIndex(-1);
    setOverIndex(-1);
  };

  const isHttpUrl = (u) => /^(https?:)\/\//i.test((u || '').trim());
  const isMailto = (u) => /^mailto:/i.test((u || '').trim());
  const isTel = (u) => /^tel:/i.test((u || '').trim());

  const validateUrlForPlatform = (platform, url) => {
    const val = (url || '').trim();
    if (platform === 'Email') return isMailto(val) || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);
    if (platform === 'Phone') return isTel(val) || /^\+?[0-9\s().-]{5,}$/.test(val);
    return isHttpUrl(val);
  };

  const save = async (e) => {
    e.preventDefault();
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (!it.social || !socialOptions.includes(it.social)) {
        toast.error(`Row ${i + 1}: platform is required`);
        return;
      }
      if (!it.url || !validateUrlForPlatform(it.social, it.url)) {
        toast.error(`Row ${i + 1}: url is invalid for ${it.social}`);
        return;
      }
    }
    try {
      setSaving(true);
      const payload = { socials: items };
      const resp = await patch(`websites/${website.id}/socials`, payload);
      const updated = resp?.website || { ...website, socials: items };
      onUpdated?.(updated);
    } catch (e) {
      toast.error('Failed to save socials.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4">
      <Form onSubmit={save}>
        {items.map((it, idx) => (
          <div
            key={idx}
            className={`drag-item${dragIndex === idx ? ' drag-item--dragging' : ''}${overIndex === idx && dragIndex !== -1 ? ' drag-item--over' : ''}`}
            onDragEnter={() => onDragEnter(idx)}
            onDragOver={onDragOver}
            onDrop={(e) => onDrop(idx, e)}
            ref={(el) => (itemRefs.current[idx] = el)}
          >
            {dragIndex !== -1 && overIndex === idx && dragIndex !== idx && (
              <div className="drop-indicator drop-indicator--top" />
            )}
            <button
              type="button"
              aria-label="Drag to reorder"
              title="Drag to reorder"
              draggable
              onDragStart={(e) => onDragStart(idx, e)}
              onDragEnd={onDragEnd}
              className="drag-handle"
              style={{ cursor: dragIndex === idx ? 'grabbing' : 'grab' }}
            >
              ⋮⋮
            </button>
            <button
              type="button"
              aria-label="Remove"
              onClick={() => removeItem(idx)}
              className="remove-btn"
              title="Remove"
            >
              ×
            </button>
            <div className="form-row-horizontal mb-2 mt-3">
              <Form.Label>Platform</Form.Label>
              <Form.Select
                value={it.social}
                onChange={(e) => updateField(idx, 'social', e.target.value)}
              >
                <option value="">Select platform</option>
                {socialOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
              </Form.Select>
            </div>
            <div className="form-row-horizontal">
              <Form.Label>URL</Form.Label>
              <Form.Control
                type="text"
                value={it.url}
                onChange={(e) => updateField(idx, 'url', e.target.value)}
                placeholder="https://... or mailto: or tel:"
              />
            </div>
          </div>
        ))}
        <div className="form-actions mb-2">
          <Button type="button" className="primary-button" size="sm" onClick={addItem}>Add social</Button>
        </div>
        <div className="form-actions">
          <Button type="submit" className="primary-button" disabled={saving}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-2" /> Saving...</>) : 'Save'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default Social;
